/**
 * StudioVersionHistory.tsx — Histórico de versões do modelo
 *
 * Painel lateral com todas as versões gravadas. Permite voltar uma versão
 * anterior ao ar (publicar de novo) reutilizando a RPC `studio_publish_version`
 * já existente — nenhuma RPC nova foi criada.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - Versão inicial (Studio, Fase 4)
 */

import React from 'react';
import { History, Loader2, Send } from 'lucide-react';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';

/** Cores da bolinha por situação da versão. */
const COR_SITUACAO: Record<string, string> = {
  published: 'bg-emerald-500',
  draft: 'bg-amber-500',
  archived: 'bg-muted-foreground',
};

const ROTULO_SITUACAO: Record<string, string> = {
  published: 'No ar',
  draft: 'Rascunho',
  archived: 'Arquivada',
};

interface VersaoResumo {
  id: string;
  version_number: number;
  status: string;
  published_at: string | null;
  created_at: string;
}

interface StudioVersionHistoryProps {
  open: boolean;
  onOpenChange: (aberto: boolean) => void;
  versions: VersaoResumo[];
  publishedVersionId?: string | null;
  publishing?: boolean;
  onPublish: (versionId: string) => void;
}

export function StudioVersionHistory({
  open,
  onOpenChange,
  versions,
  publishedVersionId,
  publishing,
  onPublish,
}: StudioVersionHistoryProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-base">
            <History className="h-4 w-4" />
            Versões do modelo
          </SheetTitle>
          <SheetDescription>
            Cada gravação vira uma versão. Você pode colocar uma versão anterior no ar novamente.
            Propostas já enviadas continuam mostrando a versão que estava no ar quando o link foi
            gerado, então publicar uma versão nova não altera o que o cliente já recebeu.
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="mt-3 h-[calc(100dvh-140px)] pr-2">
          <div className="space-y-2">
            {versions.length === 0 && (
              <p className="text-sm text-muted-foreground">Nenhuma versão gravada ainda.</p>
            )}

            {versions.map((versao) => {
              const noAr = versao.id === publishedVersionId;
              return (
                <div
                  key={versao.id}
                  className="flex items-center gap-3 rounded-lg border px-3 py-2"
                >
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${
                      COR_SITUACAO[versao.status] ?? 'bg-muted-foreground'
                    }`}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">
                      Versão {versao.version_number}
                      {noAr && (
                        <Badge variant="secondary" className="ml-2 h-4 text-[10px]">
                          atual
                        </Badge>
                      )}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {ROTULO_SITUACAO[versao.status] ?? versao.status} ·{' '}
                      {new Date(versao.published_at ?? versao.created_at).toLocaleString('pt-BR')}
                    </p>
                  </div>

                  {!noAr && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1.5 text-xs"
                      disabled={publishing}
                      onClick={() => onPublish(versao.id)}
                    >
                      {publishing ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Send className="h-3 w-3" />
                      )}
                      Colocar no ar
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
